import React from 'react';

/**
 * Third party libs
 */
import imageCompression from 'browser-image-compression';

/**
 * MUI Components
 */
import {
    Typography,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    TextField
} from '@mui/material';

/**
 * MUI Icons
 */
import { Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * Components
 */
import PhotoInputSelector from './Fields/PhotoInputSelector';

/**
 * Hooks
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const compressImage = async (file) => {
    // Define compression options
    const options = {
        maxSizeMB: 0.3,         // Maximum size in MB
        maxWidthOrHeight: 1024, // Maximum resolution width or height
        useWebWorker: true
    };

    try {
        // Compress the image
        const compressedFile = await imageCompression(file, options);

        // Create a URL for the compressed image
        const imageUrl = URL.createObjectURL(compressedFile);
        console.log('Original size:', file.size / 1024, 'KB', 'Compressed size:', compressedFile.size / 1024, 'KB');

        // Return the image URL and compressed image file
        return { imageUrl, compressedFile };
    } catch (error) {
        console.error('Error compressing image:', error);
        throw error;
    }
};

/**
 * PhotoManager component
 * @param {Object} props
 * @param {Array} props.photos
 * @param {Function} props.setPhotos
 * @param {boolean} props.requireNotes - If true, the user must enter a note for each photo
 */
const PhotoManager = ({ photos, setPhotos, requireNotes = false, enableNotes = true }) => {
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editNoteDialogOpen, setEditNoteDialogOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
    const [tempNote, setTempNote] = useState('');

    // Function to handle photo upload
    const handleAddPhoto = async (event) => {
        const files = Array.from(event.target.files);
        const compressedPhotos = [];

        // Retrieve the last note from the photos array if any
        const lastNote = photos.length ? photos[photos.length - 1].notes : '';

        for (const file of files) {
            try {
                const { imageUrl, compressedFile } = await compressImage(file);
                compressedPhotos.push({
                    imageUrl,
                    file: compressedFile,
                    notes: lastNote || ''
                });
            } catch (error) {
                console.error('Error compressing image:', error);
            }
        }

        setPhotos((prevPhotos) => [...prevPhotos, ...compressedPhotos]);
    };

    // Handle photo notes editing
    const handleEditPhotoNotes = (index) => {
        setSelectedPhotoIndex(index);
        setTempNote(photos[index]?.notes || '');
        setEditNoteDialogOpen(true);
        console.debug('Photo selected for editing:', photos[index] || index);
    };

    const handleNoteChange = (event) => {
        setTempNote(event.target.value);
    };

    const handleSavePhotoNotes = () => {
        if (selectedPhotoIndex !== null && photos[selectedPhotoIndex]) {
            setPhotos((prevPhotos) => prevPhotos.map((photo, i) => {
                if (i === selectedPhotoIndex) {
                    return { ...photo, notes: tempNote };
                }
                return photo;
            }));
        }
    };

    const handleCloseEditNoteDialog = () => {
        setEditNoteDialogOpen(false);
        setSelectedPhotoIndex(null);
    };

    const handleRemovePhoto = (index) => {
        setPhotos((prevPhotos) => prevPhotos.filter((_, i) => i !== index));
    };

    const handleClickAvatar = (imageUrl) => {
        setSelectedImageUrl(imageUrl);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedImageUrl('');
    };

    return (
        <>
            <style>
                {`
                    @keyframes blinkRed {
                        0%, 100% { color: inherit; }
                        50% { color: #f44336; }
                    }
                `}
            </style>
            {/* <input
                type="file"
                accept="image/*"
                multiple
                // capture="environment" // Prompt camera if available
                id="photo-upload"
                style={{ display: 'none' }}
                onChange={handleAddPhoto}
            /> */}
            {/* <label htmlFor="photo-upload">
                <Button
                    variant="contained"
                    color="primary"
                    component="span"
                    startIcon={<AddIcon />}
                >
                    {t('Add Photo')}
                </Button>
            </label> */}
            <PhotoInputSelector
                onFilesSelected={handleAddPhoto}
                t={t}
            />

            {requireNotes && photos.some(p => !p.notes?.trim()) && (
                <Typography color="error" align="center">
                    <WarningIcon />
                    {t('All added photos must have a note')}
                </Typography>
            )}

            <List>
                {photos.map((photo, index) => (
                    <ListItem key={index} secondaryAction={
                        <>
                            {/* Tooltip and Edit Button */}
                            {enableNotes && (
                                <Tooltip title={t('Edit note')} sx={{ marginRight: 2 }}>
                                    <IconButton
                                        edge="end"
                                        aria-label="edit"
                                        onClick={() => handleEditPhotoNotes(index)}
                                        sx={{
                                            animation: requireNotes && !photo.notes?.trim() ? 'blinkRed 1s infinite' : 'none'
                                        }}>
                                        <RateReviewIcon />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {/* Tooltip and Delete Button */}
                            <Tooltip title={t('Remove')} sx={{ marginLeft: 5 }}>
                                <IconButton edge="end" aria-label="delete" onClick={() => handleRemovePhoto(index)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    } sx={{ display: 'flex', alignItems: 'center', paddingY: 2, paddingX: 0 }}>
                        <ListItemIcon>
                            <Avatar
                                variant="rounded"
                                src={photo.imageUrl || photo.base64 || photo.url}
                                alt={`Photo ${index}`}
                                sx={{ width: 100, height: 100, objectFit: 'cover' }}
                                onClick={() => handleClickAvatar(photo.imageUrl || photo.base64 || photo.url)}
                            />
                        </ListItemIcon>
                        <ListItemText
                            sx={{
                                paddingLeft: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                flex: 1
                            }}
                            primary={<Typography variant="body2" sx={{ maxWidth: '160px' }}>{`${photo.file?.name || photo.name || '?'} ${index + 1}`}</Typography>}
                            secondary={<Typography variant="body2">{photo.notes}</Typography>} />
                    </ListItem>
                ))}
            </List>

            {/* Dialog to show larger image */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t('Photo')}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleCloseDialog}
                        aria-label="close"
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedImageUrl && (
                        <img
                            src={selectedImageUrl}
                            alt="Selected"
                            style={{ width: '100%', height: 'auto' }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit note dialog */}
            <Dialog
                open={editNoteDialogOpen}
                onClose={handleCloseEditNoteDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t('Edit note')}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleCloseEditNoteDialog}
                        aria-label="close"
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Enter a note for the photo')}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="note"
                        label={t('Note')}
                        type="textarea"
                        multiline
                        fullWidth
                        rows={4}
                        variant="standard"
                        value={tempNote}
                        onChange={handleNoteChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditNoteDialog}>{t('Cancel')}</Button>
                    <Button onClick={() => {
                        const notes = document.getElementById('note').value;
                        handleSavePhotoNotes(notes);
                        handleCloseEditNoteDialog();
                    }}>{t('Save')}</Button>
                </DialogActions>
            </Dialog>

            {/* No items found message */}
            {!photos.length && (
                <h3 style={{ textAlign: 'center' }}>{t('No items found')}</h3>
            )}
        </>
    );
};

export default PhotoManager;
